// Utility for generating fake member data

interface MemberData {
  id: string;
  name: string;
  hasPaid: boolean;
  membershipFee: number;
  dateJoined: string;
  approved: boolean;
}

// Array of business prefixes
const companyPrefixes = [
  "North",
  "South",
  "East",
  "West",
  "Central",
  "Premier",
  "Global",
  "National",
  "Metro",
  "Coastal",
  "Peak",
  "Elite",
  "United",
  "First",
  "Royal",
  "Golden",
  "Silver",
  "Advanced",
  "Modern",
  "Classic",
  "Superior",
  "Prime",
  "Capital",
  "American",
  "Pacific",
  "Atlantic",
  "Alpha",
  "Beta",
  "Omega",
  "Delta",
  "Pioneer",
  "Heritage",
  "Legacy",
  "Summit",
  "Apex",
  "Acme",
  "Zenith",
  "Innovative",
  "Precision",
  "Standard",
  "Quality",
  "Reliable",
  "Trusted",
  "Strategic",
  "Dynamic",
  "Progressive",
  "Integrated",
  "Universal",
];

// Array of business types
const companyTypes = [
  "Solutions",
  "Consulting",
  "Associates",
  "Partners",
  "Industries",
  "Systems",
  "Technologies",
  "Services",
  "Enterprises",
  "Group",
  "Agency",
  "Network",
  "Development",
  "Design",
  "Marketing",
  "Media",
  "Communications",
  "Engineering",
  "Manufacturing",
  "Construction",
  "Properties",
  "Realty",
  "Financial",
  "Insurance",
  "Investments",
  "Management",
  "Logistics",
  "Supply",
  "Healthcare",
  "Energy",
  "Resources",
  "Hospitality",
  "Foods",
  "Beverages",
  "Pharmaceuticals",
  "Electronics",
  "Software",
  "Hardware",
  "Automotive",
  "Transport",
  "Security",
  "Legal",
  "Educational",
  "Creative",
  "Digital",
  "Analytics",
  "Research",
  "Innovations",
  "Labs",
  "Ventures",
  "Capital",
  "Advisors",
];

// Array of middle parts
const companyMiddles = [
  "Tech",
  "Business",
  "Global",
  "Data",
  "Info",
  "Web",
  "Net",
  "Com",
  "Gen",
  "Pro",
  "Smart",
  "Bright",
  "Clear",
  "Fast",
  "Quick",
  "Swift",
  "Rapid",
  "Agile",
  "Nimble",
  "Secure",
  "Safe",
  "Assured",
  "Trusted",
  "Valued",
  "Core",
  "Prime",
  "Key",
  "Main",
  "Chief",
  "Top",
  "Best",
  "Better",
  "Greatest",
  "Finest",
  "Ultimate",
  "Supreme",
  "Paramount",
  "Maximum",
  "Optimal",
  "Ideal",
  "Perfect",
  "Complete",
  "Total",
  "Full",
  "Whole",
  "Entire",
  "All",
  "Every",
];

// Predefined membership fee options
const membershipFees = [200, 250, 300, 450, 600, 700, 800, 900, 1000, 1100];

// Function to generate a random company name
const generateCompanyName = (): string => {
  // 60% chance of prefix + type
  if (Math.random() < 0.6) {
    const prefix =
      companyPrefixes[Math.floor(Math.random() * companyPrefixes.length)];
    const type = companyTypes[Math.floor(Math.random() * companyTypes.length)];
    return `${prefix} ${type}`;
  }
  // 40% chance of prefix + middle + type
  else {
    const prefix =
      companyPrefixes[Math.floor(Math.random() * companyPrefixes.length)];
    const middle =
      companyMiddles[Math.floor(Math.random() * companyMiddles.length)];
    const type = companyTypes[Math.floor(Math.random() * companyTypes.length)];
    return `${prefix} ${middle} ${type}`;
  }
};

// Function to generate a random fee from predefined options
const generateFee = (): number => {
  return membershipFees[Math.floor(Math.random() * membershipFees.length)];
};

// Function to generate a date in the past couple years
const generateDate = (): string => {
  const end = new Date();
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - 2); // Go back two years

  const date = new Date(
    start.getTime() + Math.random() * (end.getTime() - start.getTime())
  );

  // Format as "Jan 12, 2022"
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Generate an array of fake members
export const generateFakeMemberData = (count: number): MemberData[] => {
  const members: MemberData[] = [];

  for (let i = 0; i < count; i++) {
    members.push({
      id: `mem-${i + 1}`,
      name: generateCompanyName(),
      hasPaid: Math.random() > 0.3, // 70% have paid
      membershipFee: generateFee(),
      dateJoined: generateDate(),
      approved: Math.random() > 0.2, // 80% are approved
    });
  }

  return members;
};

// Generate invoice data for a member
export const generateInvoiceData = (member: MemberData) => {
  return {
    invoiceNumber: `INV-${Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, "0")}`,
    issueDate: member.dateJoined,
    dueDate: member.dateJoined, // Same as join date for simplicity
    companyName: member.name,
    amount: member.membershipFee,
    status: member.hasPaid ? "Paid" : "Unpaid",
    items: [
      {
        description: "Annual Chamber Membership",
        quantity: 1,
        unitPrice: member.membershipFee,
        total: member.membershipFee,
      },
    ],
  };
};
